import { OrderData } from "@nusrif/types";
import { Button } from "../../ui/button";
import { Package, Truck, Clock, X } from "lucide-react";
import { formatCurrency } from "@nusrif/utils";
import config from "@nusrif/config";

interface OrderCardProps {
  order: OrderData;
  onTrackOrder: (orderId: string) => void;
  onReorder: (orderId: string) => void;
  onViewDetails: (orderId: string) => void;
  orderId: string;
  id: string;
}

export const OrderCard = ({
  order,
  onTrackOrder,
  onReorder,
  onViewDetails,
  orderId,
  id,
}: OrderCardProps) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "delivered":
        return <Package className="w-4 h-4 text-green-600" />;
      case "shipped":
        return <Truck className="w-4 h-4 text-blue-600" />;
      case "processing":
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case "cancelled":
        return <X className="w-4 h-4 text-red-600" />;
      default:
        return <Package className="w-4 h-4 text-gray-600" />;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "text-green-600 bg-green-50";
      case "shipped":
        return "text-blue-600 bg-blue-50";
      case "processing":
        return "text-yellow-600 bg-yellow-50";
      case "cancelled":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const canTrack = order.status === "Shipped" || order.status === "Delivered";
  const canReorder =
    order.status === "Delivered" || order.status === "Cancelled";

  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-4 space-y-4">
      {/* Order Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-black">{orderId}</p>
          <p className="text-sm text-gray-600">{formatDate(order.orderDate)}</p>
        </div>
        <div
          className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}
        >
          {getStatusIcon(order.status)}
          <span className="capitalize">
            {order.status === "PaymentSettled" ? "In Progress" : order.status}
          </span>
        </div>
      </div>
      {/* Order Items */}
      <div className="space-y-3">
        {order.items.map((item) => (
          <div key={item.id} className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <p className="font-medium text-black text-sm line-clamp-1">
                {item.name}
              </p>
              <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
            </div>
            <p className="font-medium text-black">
              {formatCurrency(item.price, config.region.currencyCode)}
            </p>
          </div>
        ))}
      </div>
      {/* Order Total */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <span className="font-medium text-black">Total</span>
        <span className="font-bold text-[#1e4aa8] text-lg">
          {formatCurrency(order.finalTotal, config.region.currencyCode)}
        </span>
      </div>
      {/* Action Buttons */}
      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 h-10"
          onClick={() => onViewDetails(id)}
        >
          View Details
        </Button>

        {canTrack && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-10 text-primary-blue border-primary-blue"
            onClick={() => onTrackOrder(orderId)}
          >
            Track Order
          </Button>
        )}

        {canReorder && (
          <Button
            size="sm"
            className="flex-1 h-10"
            onClick={() => onReorder(orderId)}
          >
            Reorder
          </Button>
        )}
      </div>
      {/* Tracking Number */}
      {order.trackingCode && (
        <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
          Tracking Code: {order.trackingCode}
        </div>
      )}
    </div>
  );
};

// Skeleton loader for OrderCard
export const OrderCardSkeleton = () => {
  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-4 space-y-4 animate-pulse">
      {/* Order Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
          <div className="h-3 w-16 bg-gray-200 rounded" />
        </div>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full w-24 h-6 bg-gray-200" />
      </div>
      {/* Order Items */}
      <div className="space-y-3">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-lg" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-24 bg-gray-200 rounded" />
              <div className="h-2 w-12 bg-gray-200 rounded" />
            </div>
            <div className="h-4 w-10 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
      {/* Order Total */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="h-4 w-16 bg-gray-200 rounded" />
        <div className="h-5 w-16 bg-gray-200 rounded" />
      </div>
      {/* Action Buttons */}
      <div className="flex gap-3 pt-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex-1 h-10 bg-gray-200 rounded" />
        ))}
      </div>
      {/* Tracking Number */}
      <div className="h-3 w-32 bg-gray-200 rounded mt-2" />
    </div>
  );
};
